import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import CloseIcon from '@mui/icons-material/CloseRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import moment from 'moment';
import toast from 'react-hot-toast';
import { formateurSurveyApi } from '../../../../api/formateurSurveyApi';
import Scrollbar from '../../../Scrollbar';
import ScrollJumpButtons from '../../../ScrollJumpButtons';
import { ScoreRing } from './SessionResponseCard';
import StarRating from './StarRating';
import { answerValues, formatDateRange, formatStyle, renderAnswer, scoreColor } from './constants';
import { alpha } from '@mui/material/styles';
import { grey, getTone } from '../../../../theme/tones';

/** Hauteur de la Toolbar de DashboardNavbar (minHeight: 64). */
const NAVBAR_HEIGHT = 64;

const parseAnswers = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return {};
  }
};

const parseOptions = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

/**
 * Détail d'UNE réponse de formateur.
 *
 * Le tiroir des apprenants est un tableau : une ligne par répondant, une
 * colonne par question. Ici il n'y a qu'un répondant par session — on déroule
 * donc le formulaire tel qu'il a été rempli, question après question, ce qui
 * reste lisible même avec les 37 questions du modèle présentiel.
 */
const FormateurResponseDrawer = ({ submissionId, accent, onClose, onDeleted }) => {
  const [row, setRow] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const scrollRef = useRef(null);
  const getScroller = useCallback(() => scrollRef.current, []);

  useEffect(() => {
    if (!submissionId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const accessToken = window.localStorage.getItem('accessToken');
        const res = await formateurSurveyApi.fetchSubmission(submissionId, accessToken);
        if (cancelled) return;
        if (res && res.status) {
          setRow(res.data);
          setQuestions(res.questions || []);
        } else {
          setError((res && res.message) || 'Chargement impossible.');
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [submissionId]);

  const answers = useMemo(() => parseAnswers(row && row.answers), [row]);

  /**
   * Les questions du modèle, dans l'ordre du formulaire, avec la réponse et —
   * pour les questions à choix — le poids de l'option retenue : c'est lui qui
   * fait la note, il doit rester vérifiable à l'œil.
   */
  const lines = useMemo(() => questions.map((q) => {
    const options = parseOptions(q.options);
    const values = answerValues(answers[q.id]);
    let weight = null;

    if (options.length && values.length) {
      const match = options.find((o) => String(o && o.text !== undefined ? o.text : o).trim() === values[0]);
      if (match && match.weight !== undefined && match.weight !== null) weight = Number(match.weight);
    }

    return {
      id: q.id,
      text: q.text,
      isChoice: options.length > 0,
      answered: values.length > 0,
      answer: renderAnswer(answers[q.id]),
      weight
    };
  }), [questions, answers]);

  const answeredCount = lines.filter((l) => l.answered).length;

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette réponse ?')) return;
    try {
      setDeleting(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await formateurSurveyApi.deleteSubmission(submissionId, accessToken);
      if (res && res.status) {
        toast.success('Réponse supprimée');
        if (onDeleted) onDeleted();
        onClose();
      } else {
        toast.error((res && res.message) || 'Suppression impossible');
      }
    } catch (err) {
      toast.error('Erreur technique');
    } finally {
      setDeleting(false);
    }
  };

  const fmt = row ? formatStyle(row.scope || row.format) : null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(submissionId)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: '70vw' },
          maxWidth: 1000,
          bgcolor: grey(50),
          backgroundImage: 'none',
          // La barre de navigation du dashboard a un zIndex supérieur à celui
          // du Drawer : sans ce décalage elle recouvre l'en-tête du tiroir.
          top: NAVBAR_HEIGHT,
          height: `calc(100% - ${NAVBAR_HEIGHT}px)`
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* En-tête */}
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderBottom: `1px solid ${grey(200)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
              <ScoreRing value={row ? row.average : null} size={72} />
              <StarRating percent={row ? row.average : null} size="0.78rem" color={scoreColor(row ? row.average : null)} iconSize="0.95rem" />
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                <Chip
                  label="Formateur"
                  size="small"
                  sx={{ bgcolor: `${accent}1F`, color: accent, fontWeight: 800, fontSize: '0.65rem', height: 21 }}
                />
                {row && row.scope && (
                  <Chip
                    label={row.scope}
                    size="small"
                    sx={{ bgcolor: fmt.bg, color: fmt.color, fontWeight: 800, fontSize: '0.65rem', height: 21 }}
                  />
                )}
                {row && (
                  <Chip
                    label={`Session ${row.session_number ?? '—'}`}
                    size="small"
                    sx={{ bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary', fontWeight: 800, fontSize: '0.65rem', height: 21 }}
                  />
                )}
              </Box>

              <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: 'text.primary', lineHeight: 1.3 }}>
                {row ? (row.formateur || 'Formateur inconnu') : '…'}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', fontWeight: 600, mt: 0.35 }}>
                {row ? (row.formation_name || 'Formation inconnue') : ''}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600, mt: 0.3 }}>
                {row && formatDateRange(row.start_date, row.end_date)}
                {row && (row.meeting_town || row.meeting_place_name) && ` · ${row.meeting_town || row.meeting_place_name}`}
                {row && ` · répondu le ${moment(row.created_at).format('DD/MM/YYYY [à] HH:mm')}`}
                {row && ` · ${answeredCount}/${lines.length} question${lines.length > 1 ? 's' : ''} renseignée${answeredCount > 1 ? 's' : ''}`}
              </Typography>
              {row && row.email && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 600, mt: 0.2 }}>
                  {row.email}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                onClick={handleDelete}
                disabled={deleting || !row}
                variant="outlined"
                startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, borderColor: getTone('orange').light, color: 'error.main' }}
              >
                Supprimer
              </Button>
              <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Contenu */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 2.5, position: 'relative' }}>
          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
              <CircularProgress sx={{ color: accent }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
              <Typography sx={{ color: 'error.main', fontWeight: 700 }}>{error}</Typography>
            </Box>
          )}

          {!loading && !error && (
            <>
              <ScrollJumpButtons
                getTarget={getScroller}
                watch={`formateur-${submissionId}-${lines.length}`}
                accent={accent}
                offset={14}
              />
              <Scrollbar containerRef={(el) => { scrollRef.current = el; }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pr: 1, pb: 2 }}>
                  {lines.map((line, index) => (
                    <Box
                      key={line.id}
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: '16px',
                        border: `1px solid ${grey(200)}`,
                        opacity: line.answered ? 1 : 0.6
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: grey(300), minWidth: 26 }}>
                          {String(index + 1).padStart(2, '0')}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', flexGrow: 1 }}>
                          {line.text}
                        </Typography>
                        {line.weight !== null && (
                          <Tooltip title="Poids de l'option choisie dans la note globale">
                            <Box
                              sx={{
                                px: 1, py: 0.2, borderRadius: '8px', flexShrink: 0,
                                bgcolor: `${scoreColor(line.weight)}1A`,
                                color: scoreColor(line.weight),
                                fontWeight: 900, fontSize: '0.68rem'
                              }}
                            >
                              {line.weight}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>

                      <Typography
                        sx={{
                          mt: 0.75,
                          ml: 4.25,
                          fontSize: '0.85rem',
                          fontWeight: line.isChoice ? 700 : 500,
                          color: line.answered ? grey(700) : grey(400),
                          fontStyle: line.isChoice || !line.answered ? 'normal' : 'italic',
                          borderLeft: line.isChoice ? 'none' : `3px solid ${accent}33`,
                          pl: line.isChoice ? 0 : 1.5,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {line.answered ? line.answer : 'Sans réponse'}
                      </Typography>
                    </Box>
                  ))}

                  {lines.length === 0 && (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <Typography sx={{ color: 'text.disabled', fontWeight: 700 }}>Aucune question.</Typography>
                    </Box>
                  )}
                </Box>
              </Scrollbar>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

FormateurResponseDrawer.propTypes = {
  submissionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  accent: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func
};

export default FormateurResponseDrawer;
